// Quarter Asian lines (e.g. -0.75, Over 2.25) settle as two half-stake bets, so a tip can be
// HALF_WON / HALF_LOST, and whole lines (0, -1, Over 3.0) can PUSH (stake returned).
// Uses plain values (no @app/graphql import) so the standalone OG image bundle can share it.

const TipResult = {
  Won: 'WON',
  HalfWon: 'HALF_WON',
  Lost: 'LOST',
  HalfLost: 'HALF_LOST',
  Push: 'PUSH',
  Void: 'VOID',
} as const

type Result = string | null | undefined

export const isWonResult = (result: Result): boolean =>
  result === TipResult.Won || result === TipResult.HalfWon

export const isLostResult = (result: Result): boolean =>
  result === TipResult.Lost || result === TipResult.HalfLost

export const isNeutralResult = (result: Result): boolean =>
  result === TipResult.Void || result === TipResult.Push

export const resultLabelKey = (result: Result): string => {
  switch (result) {
    case TipResult.Won:
      return 'common.won'
    case TipResult.HalfWon:
      return 'common.halfWon'
    case TipResult.Lost:
      return 'common.lost'
    case TipResult.HalfLost:
      return 'common.halfLost'
    case TipResult.Push:
      return 'common.push'
    default:
      return 'common.void'
  }
}

const round = (value: number): number => Number(value.toFixed(2))

// Profit in units for a single tip. A half win is half the stake times (odds - 1), not half a unit.
export const tipProfit = (result: Result, stake: number, odds: number): number => {
  switch (result) {
    case TipResult.Lost:
      return -stake
    case TipResult.HalfLost:
      return round(-stake / 2)
    case TipResult.HalfWon:
      return round((stake * odds - stake) / 2)
    case TipResult.Void:
    case TipResult.Push:
      return 0
    default:
      return round(stake * odds - stake)
  }
}

// Multitip odds are effective odds after settlement (half / push legs already folded in).
export const multitipProfit = (result: Result, stake: number, odds: number): number => {
  if (result === TipResult.Lost) return -stake
  if (isNeutralResult(result)) return 0
  return round(stake * odds - stake)
}

export const formatProfit = (profit: number): string => {
  if (profit > 0) return `+${profit}u`
  if (profit < 0) return `${profit}u`
  return '0u'
}

// Asian handicap / total lines are valid in 0.25 steps (half, whole and quarter lines).
export const isQuarterStepLine = (line: number | string | null | undefined): boolean => {
  if (line === null || line === undefined || line === '') return false
  const value = Number(line)
  if (!Number.isFinite(value)) return false
  return Math.abs(value * 4 - Math.round(value * 4)) < 1e-9
}

// Splits a quarter line into its two halves for display, nearest to zero first: -0.75 -> "-0.5 / -1.0".
// Returns null for half / whole lines.
export const quarterLineSplit = (line: number | string | null | undefined): string | null => {
  if (line === null || line === undefined || line === '') return null
  const value = Number(line)
  if (!Number.isFinite(value)) return null
  const quarters = Math.round(value * 4)
  if (Math.abs(value * 4 - quarters) > 1e-9 || quarters % 2 === 0) return null
  const showPlus = String(line).trim().startsWith('+')
  const format = (q: number) => {
    const v = q / 4
    if (v === 0) return '0'
    const text = Number.isInteger(v) ? v.toFixed(1) : String(v)
    return showPlus && v > 0 ? `+${text}` : text
  }
  const [ near, far ] = [ quarters - 1, quarters + 1 ].sort((a, b) => Math.abs(a) - Math.abs(b))
  return `${format(near)} / ${format(far)}`
}
